import { type AuthorKind, briefVersionReasonText } from '@sigora/domain';
import { useEffect, useId, useRef } from 'react';

import type { BriefHistoryEntryView } from '../../api/client.ts';
import {
  CURRENT_TAG,
  HIDE_HISTORY_LABEL,
  ORIGINAL_TAG,
  VIEW_VERSION_LABEL,
  briefHistoryLine,
  briefVersionLine,
  historyToggleLabel,
} from '../brief-copy.ts';
import { briefDate } from './brief-date.ts';

/** What wrote a version, as its history line says it. */
const reasonOf = (entry: Pick<BriefHistoryEntryView, 'reason' | 'authoredBy'>): string =>
  briefVersionReasonText({ reason: entry.reason, authoredBy: entry.authoredBy });

/**
 * The Brief's version line — the version on screen and what wrote it — and, once there is more
 * than one version, its history behind a toggle: every version newest first, with what wrote it and
 * when, the publication tagged as the original and the newest as current. Every version but the
 * current one, and the one already open, can be opened (`onView`).
 *
 * The step holds what is open — the history, the version — so both outlive this block being drawn
 * again. `restoreFocusTo` names a version whose View takes the focus back once its view closes; if
 * that View can't take it (the history was closed meanwhile), the toggle does. `onFocusRestored`
 * says the focus has gone back.
 */
export const BriefHistory = ({
  version,
  authoredBy,
  history,
  open,
  onToggle,
  viewing,
  onView,
  restoreFocusTo,
  onFocusRestored,
}: {
  /** The current version, and who wrote it. */
  version: number;
  authoredBy: AuthorKind;
  /** Every version's history line, oldest first. */
  history: readonly BriefHistoryEntryView[];
  open: boolean;
  onToggle: () => void;
  viewing: number | null;
  onView: (version: number) => void;
  restoreFocusTo: number | null;
  onFocusRestored: () => void;
}) => {
  const listId = useId();
  const toggle = useRef<HTMLButtonElement | null>(null);
  const viewButtons = useRef(new Map<number, HTMLButtonElement>());

  useEffect(() => {
    if (restoreFocusTo === null) {
      return;
    }
    const button = viewButtons.current.get(restoreFocusTo);
    if (button !== undefined && button.closest('[hidden]') === null) {
      button.focus();
    } else {
      toggle.current?.focus();
    }
    onFocusRestored();
  }, [restoreFocusTo, onFocusRestored]);

  const current = history.find((entry) => entry.version === version) ?? {
    reason: null,
    authoredBy,
  };

  return (
    <div className="brief-versions">
      <p className="artifact-meta" data-test-id="brief-version">
        {briefVersionLine(version, reasonOf(current))}
        {history.length > 1 && (
          <>
            {' '}
            <button
              type="button"
              className="text-link muted"
              aria-expanded={open}
              aria-controls={listId}
              data-test-id="brief-history-toggle"
              ref={toggle}
              onClick={onToggle}
            >
              {open ? HIDE_HISTORY_LABEL : historyToggleLabel(history.length)}
            </button>
          </>
        )}
      </p>
      {history.length > 1 && (
        // In the page while closed, so the toggle's `aria-controls` always names an element.
        // Newest first and `reversed`, so an item's position counts down with its version; the
        // number shown is the version in each line's own words.
        <ol
          className="brief-history"
          id={listId}
          data-test-id="brief-history"
          reversed
          hidden={!open}
        >
          {history.toReversed().map((entry) => {
            const lineId = `${listId}-${String(entry.version)}`;
            const isCurrent = entry.version === version;
            const isViewed = entry.version === viewing;

            return (
              <li
                key={entry.version}
                data-test-id="brief-history-line"
                data-version={entry.version}
                aria-current={isViewed ? 'true' : undefined}
              >
                <span id={lineId}>{briefHistoryLine(entry.version, reasonOf(entry))}</span>
                {entry.version === 1 && (
                  <>
                    {' '}
                    <span className="brief-tag" data-test-id="brief-history-original">
                      {ORIGINAL_TAG}
                    </span>
                  </>
                )}
                {isCurrent && (
                  <>
                    {' '}
                    <span className="brief-tag" data-test-id="brief-history-current">
                      {CURRENT_TAG}
                    </span>
                  </>
                )}{' '}
                <span className="artifact-meta">{briefDate(entry.createdAt)}</span>
                {!isCurrent && !isViewed && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="text-link brief-inline-action"
                      data-test-id="brief-history-view"
                      aria-describedby={lineId}
                      ref={(button) => {
                        if (button === null) {
                          viewButtons.current.delete(entry.version);
                        } else {
                          viewButtons.current.set(entry.version, button);
                        }
                      }}
                      onClick={() => onView(entry.version)}
                    >
                      {VIEW_VERSION_LABEL}
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
